/**
 * @fileoverview Hook de disparo em massa durável (mass-send). Cobre:
 *  - validação prévia (POST /mass-send/validate)
 *  - envio de teste (POST /mass-send/test)
 *  - busca da execução mais recente do fluxo (GET /mass-send/executions)
 *  - pausar/continuar/cancelar (POST /mass-send/executions/:id/:action)
 *  - monitoramento ao vivo via socket (company-<id>-massSendProgress)
 *
 * Normaliza tanto a resposta REST (counts aninhado, campo `id`) quanto o
 * payload do socket (campos achatados, `executionId`) num formato único.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import api from "../../../services/api";
import openSocket from "../../../services/socket-io";
import { canNow } from "../../../hooks/usePermission";

/** Converte qualquer forma (REST ou socket) num objeto de execução único. */
export function normalizeExecution(e) {
  if (!e) return null;
  const id = e.id != null ? e.id : e.executionId;
  const src = e.counts || e;
  return {
    id,
    status: e.status || null,
    pauseReason: e.pauseReason || null,
    isAutoPaused: !!e.isAutoPaused,
    nextResumeAt: e.nextResumeAt || null,
    templateName: e.templateName || null,
    done: !!e.done,
    maxSendsPerDay: e.maxSendsPerDay != null ? e.maxSendsPerDay : null,
    counts: {
      total: src.total || 0,
      pending: src.pending || 0,
      processing: src.processing || 0,
      sent: src.sent || 0,
      delivered: src.delivered || 0,
      read: src.read || 0,
      failed: src.failed || 0,
      ignored: src.ignored || 0,
      skipped: src.skipped || 0,
    },
  };
}

export default function useMassSend(workflowId) {
  const [execution, setExecution] = useState(null);
  const [loading, setLoading] = useState(false);
  const mountedRef = useRef(true);

  const fetchLatest = useCallback(
    async (wfId) => {
      const id = wfId || workflowId;
      if (!id) return null;
      // Sem acompanhar disparos (mass_send.view, com a regra nova ligada), nem pede.
      if (!canNow("mass_send.view", true)) {
        if (mountedRef.current) setExecution(null);
        return null;
      }
      setLoading(true);
      try {
        const { data } = await api.get("/mass-send/executions", {
          params: { workflowId: id },
        });
        const list = (data && data.executions) || [];
        const latest = list.length ? normalizeExecution(list[0]) : null;
        if (mountedRef.current) setExecution(latest);
        return latest;
      } catch (err) {
        return null;
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    },
    [workflowId]
  );

  const validate = useCallback(async (payload) => {
    const { data } = await api.post("/mass-send/validate", payload);
    return data;
  }, []);

  const sendTest = useCallback(async (payload) => {
    const { data } = await api.post("/mass-send/test", payload);
    return data;
  }, []);

  const doAction = useCallback(async (execId, action) => {
    const { data } = await api.post(`/mass-send/executions/${execId}/${action}`);
    if (data && mountedRef.current) setExecution(normalizeExecution(data));
    return data;
  }, []);

  const pause = useCallback((id) => doAction(id, "pause"), [doAction]);
  const resume = useCallback((id) => doAction(id, "resume"), [doAction]);
  const cancel = useCallback((id) => doAction(id, "cancel"), [doAction]);

  /** Lista destinatários da execução, filtrável por status (paginado 50). */
  const fetchRecipients = useCallback(async (execId, opts) => {
    const { status, pageNumber = 1 } = opts || {};
    const { data } = await api.get(
      `/mass-send/executions/${execId}/recipients`,
      { params: { status: status || undefined, pageNumber } }
    );
    return data; // { count, hasMore, recipients }
  }, []);

  /** Edita a config de uma execução já criada (ex.: máximo por dia). */
  const updateExecution = useCallback(async (execId, patch) => {
    const { data } = await api.put(`/mass-send/executions/${execId}`, patch);
    if (data && mountedRef.current) setExecution(normalizeExecution(data));
    return data;
  }, []);

  /* ── Socket ao vivo ─────────────────────────────
     Filtra por workflowId quando o payload o traz. Como fallback (payload sem
     workflowId), adota a execução por id: mesmo id (atualização live) ou id
     estritamente maior (novo disparo). A busca ao abrir o painel corrige
     eventuais cruzamentos entre fluxos. */
  useEffect(() => {
    mountedRef.current = true;
    const companyId = localStorage.getItem("companyId");
    if (!companyId) return undefined;
    const socket = openSocket();
    const handler = (payload) => {
      if (!payload) return;
      // O payload traz workflowId — ignora eventos de outros fluxos.
      if (
        workflowId != null &&
        payload.workflowId != null &&
        String(payload.workflowId) !== String(workflowId)
      ) {
        return;
      }
      const next = normalizeExecution(payload);
      if (!next || next.id == null) return;
      setExecution((prev) => {
        if (prev && String(prev.id) !== String(next.id)) {
          if (!(Number(next.id) > Number(prev.id))) return prev;
        }
        if (prev && String(prev.id) === String(next.id) && next.templateName == null) {
          next.templateName = prev.templateName;
        }
        // O payload do socket não traz maxSendsPerDay — preserva o já conhecido.
        if (prev && String(prev.id) === String(next.id) && next.maxSendsPerDay == null) {
          next.maxSendsPerDay = prev.maxSendsPerDay;
        }
        return next;
      });
    };
    socket.on(`company-${companyId}-massSendProgress`, handler);
    return () => {
      mountedRef.current = false;
      socket.off(`company-${companyId}-massSendProgress`, handler);
    };
  }, [workflowId]);

  return {
    execution,
    setExecution,
    loading,
    fetchLatest,
    validate,
    sendTest,
    pause,
    resume,
    cancel,
    fetchRecipients,
    updateExecution,
  };
}
